import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { ErrorBoundary } from '@/app/ErrorBoundary';
import { router } from '@/app/router';
import { Spinner } from '@/components/ui/feedback';
import i18n from '@/i18n';
import { friendlyError } from '@/lib/errors';
import { initMonitoring, reportError } from '@/lib/monitoring';
import { initTheme, useResolvedTheme } from '@/lib/theme';
import './styles.css';

void initMonitoring();
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: true },
  },
  queryCache: new QueryCache({ onError: (e) => reportError(e) }),
  mutationCache: new MutationCache({
    onError: (e) => {
      reportError(e);
      toast.error(friendlyError(e, i18n.t));
    },
  }),
});

function App() {
  const auth = useAuth();
  // Re-run route guards (login redirect, permission checks) whenever auth changes.
  useEffect(() => {
    if (!auth.loading) void router.invalidate();
  }, [auth.loading, auth.session, auth.perms]);
  if (auth.loading) return <Spinner className="min-h-dvh" />;
  return <RouterProvider router={router} context={{ auth }} />;
}

function ThemedToaster() {
  const theme = useResolvedTheme();
  return (
    <Toaster
      position="top-center"
      richColors
      dir="auto"
      theme={theme}
      toastOptions={{ className: '!rounded-2xl !shadow-pop !font-sans' }}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
          <ThemedToaster />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
